import {
  FocusEvent,
  Fragment,
  ReactElement,
  ReactNode,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';

import { isValidPhoneNumber } from 'libphonenumber-js';
import styled from 'styled-components';

import { FormError } from '@components/form-error';
import { FieldDescription, FormLabel } from '@components/form-label';
import { IconProps } from '@components/icons';
import { MaskedInput } from '@components/masked-input';
import { Select } from '@components/select';
import { Stack } from '@components/stack';
import { InputContainer } from '@components/text-field/text-field';
import { VisuallyHidden } from '@components/visually-hidden';
import { useEvent } from '@hooks/use-event';
import { TranslationsOverride, useScopedTranslate } from '@locale/translate';
import { theme } from '@styles/theme';

import { countryOptions, CountryPhoneInfo } from './country-data';
import {
  optionFilter,
  parseCountryMask,
  parsePhone,
  parsePhoneAndFindCountry,
  serializePhoneNumber,
} from './utils';

const EMPTY_CHAR = '\u2008';

export type PhoneFieldProps = TranslationsOverride<typeof translations> & {
  error?: string;
  label?: string;
  hideLabel?: boolean;
  onBlur?(event?: FocusEvent): void;
  onChange?(value: string): void;
  defaultValue?: string;
  isDisabled?: boolean;
  portal?: boolean;
  labelIcon?: ReactElement<IconProps>;
  labelIconAfter?: ReactNode;
  isCountryPrefilled?: boolean;
  description?: string;
  isAutoFocused?: boolean;
};

export const PhoneField = ({
  error,
  label,
  hideLabel = false,
  translationsOverride,
  onBlur,
  onChange,
  defaultValue,
  isDisabled = false,
  portal,
  labelIcon,
  labelIconAfter,
  isCountryPrefilled = false,
  description,
  isAutoFocused = false,
}: PhoneFieldProps) => {
  const { t } = useScopedTranslate(translations, translationsOverride);
  const inputRef = useRef<MaskedInput>(null);
  const [{ selectedCountry, phone }, setState] = useState<{
    selectedCountry?: CountryPhoneInfo;
    phone: string;
  }>(() => parsePhone(defaultValue, isCountryPrefilled));

  const handleChange = useEvent((value: string) => {
    onChange?.(serializePhoneNumber(value));
  });

  const shouldFocusAfterCountryChange = useRef(false);

  const onCountrySelect = useCallback(
    (value: string) => {
      const country = countryOptions.find((c) => c.value === value);
      if (!country) return;
      shouldFocusAfterCountryChange.current = true;
      if (!phone) {
        setState({ phone: '', selectedCountry: country });
        handleChange('');
        return;
      }

      const combined = serializePhoneNumber(country.code + phone);
      // If the previously entered phone number isn't valid with the
      // new country code, just reset the phone number
      const isValid = isValidPhoneNumber(combined);

      setState({
        phone: isValid ? phone : '',
        selectedCountry: country,
      });

      handleChange(isValid ? combined : serializePhoneNumber(country.code));
    },
    [handleChange, phone],
  );

  const onPhoneInput = useCallback(
    (value: string) => {
      if (!value) {
        setState((old) => ({ ...old, phone: '' }));
        handleChange('');
        return;
      }

      if (!selectedCountry) return;

      const newCountry = parsePhoneAndFindCountry(value, selectedCountry) ?? selectedCountry;

      setState({
        phone: value,
        selectedCountry: newCountry,
      });

      handleChange(newCountry.code + value);
    },
    [handleChange, selectedCountry],
  );

  // The number input is disabled until a country is picked, so a field that
  // starts out empty hands the focus to the country select instead.
  const isCountryAutoFocused = isAutoFocused && !selectedCountry;

  useEffect(() => {
    if (!isAutoFocused || isCountryAutoFocused) return;
    inputRef.current?.inputElement?.focus?.({ preventScroll: true });
    // oxlint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!shouldFocusAfterCountryChange.current) return;
    requestAnimationFrame(() => {
      inputRef.current?.inputElement?.focus?.({ preventScroll: true });
      shouldFocusAfterCountryChange.current = false;
    });
  }, [selectedCountry?.code]);

  const FormLabelContainer = hideLabel ? VisuallyHidden : Fragment;

  return (
    <Stack space={1}>
      <FormLabelContainer>
        <FormLabel text={label ?? t('label')} icon={labelIcon} iconAfter={labelIconAfter} />
      </FormLabelContainer>
      <FieldDescription text={description} />
      <Stack space={1}>
        <Stack space={2} orientation="horizontal">
          <CountryCodeContainer data-error={Boolean(error)} data-testid="phone-country-select">
            <Select
              menuPortalTarget={portal ? document.body : undefined}
              placeholder={t('placeholder')}
              options={countryOptions}
              onChange={onCountrySelect}
              isClearable={false}
              filterOption={optionFilter}
              value={selectedCountry?.value}
              // @ts-ignore the option types only know about label and value
              parseSingleValue={(option) => `${option.country} +${option.code}`}
              overflowWidth
              isDisabled={isDisabled}
              isAutoFocused={isCountryAutoFocused}
            />
          </CountryCodeContainer>
          <PhoneNumberContainer data-error={Boolean(error)} data-disabled={isDisabled}>
            <MaskedInput
              ref={inputRef}
              key={String(selectedCountry?.code)}
              mask={selectedCountry ? parseCountryMask(selectedCountry) : false}
              onTextChange={onPhoneInput}
              placeholder={selectedCountry?.mask.replaceAll('.', '_')}
              placeholderChar={selectedCountry?.mask ? '_' : EMPTY_CHAR}
              disabled={!selectedCountry || isDisabled}
              defaultValue={phone}
              onBlur={onBlur}
              data-testid="phone-number-input"
            />
          </PhoneNumberContainer>
        </Stack>
        <FormError text={error} />
      </Stack>
    </Stack>
  );
};

const CountryCodeContainer = styled.div({
  minWidth: '8rem',
  borderWidth: theme.form.border.width,
  borderRadius: theme.form.border.radius,
  borderColor: theme.form.border.color,
  borderStyle: 'solid',
  height: theme.base.height,
  '&[data-error="true"]': {
    borderColor: theme.color.elementRed100,
    color: theme.color.elementRed100,
    background: theme.color.redTrans5,
  },
  '> div': {
    margin: `calc(${theme.form.border.width} * -1)`,
  },
});

const PhoneNumberContainer = styled(InputContainer)({ flexGrow: 1 });

const translations = {
  is: {
    label: 'Símanúmer',
    placeholder: 'Land',
  },
  en: {
    label: 'Telephone number',
    placeholder: 'Country',
  },
};
